import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { PartialOptions } from 'overlayscrollbars';
import { api } from '@/api/client';
import { useStudiosCatalogSearch } from '@/contexts/StudiosCatalogSearchContext';
import { Button } from '@/components/ui/Button/Button';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { StudioEditableCell } from '@/components/StudiosTable/StudioEditableCell';
import { STUDIOS_TABLE_QUERY_KEY } from '@/components/StudiosTable/studiosTableQueryKey';
import { STUDIO_COLUMNS, STUDIOS_GRID_COLUMNS } from '@/components/StudiosTable/studiosTableLayout';
import {
  highlightPlainText,
  parseSearchHighlightWords,
  SEARCH_HIGHLIGHT_CLASS_READERS_CATALOG,
} from '@/utils/highlightSearchTerms';
import '@/components/ProjectsTable/ProjectsTable.css';
import './StudiosTable.css';

const PAGE_SIZE = 50;

export { STUDIOS_TABLE_QUERY_KEY };

const STUDIOS_TABLE_SCROLL_OS_OPTIONS: PartialOptions = {
  overflow: { x: 'scroll', y: 'scroll' },
};

function EmptyNa() {
  return (
    <div className="studios-table-catalog__na-wrap">
      <span className="projects-table__empty-value">N/A</span>
    </div>
  );
}

export function StudiosTable() {
  const { debouncedSearch } = useStudiosCatalogSearch();

  const listQuery = useInfiniteQuery({
    queryKey: [...STUDIOS_TABLE_QUERY_KEY, debouncedSearch],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const res = await api.studios.list(debouncedSearch, {
        limit: PAGE_SIZE,
        offset: pageParam,
      });
      return res;
    },
    getNextPageParam: (last, pages) => {
      if (!last.hasMore) return undefined;
      return pages.reduce((sum, p) => sum + p.items.length, 0);
    },
  });

  const studios = useMemo(
    () => listQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [listQuery.data]
  );

  const searchHighlightWords = useMemo(
    () => (debouncedSearch.trim() ? parseSearchHighlightWords(debouncedSearch) : []),
    [debouncedSearch]
  );

  const tableListLoading = listQuery.isPending && !listQuery.data;
  const tableListError = listQuery.isError;
  const searchActive = debouncedSearch.trim().length > 0;

  return (
    <div className="projects-table studios-table-catalog" role="table" aria-label="Студии">
      <OverlayScrollArea
        className="projects-table__scroll"
        defer={false}
        osOptions={STUDIOS_TABLE_SCROLL_OS_OPTIONS}
      >
        <div className="projects-table__inner studios-table-catalog__inner">
          <div
            className="projects-table__header-row"
            role="row"
            style={{ gridTemplateColumns: STUDIOS_GRID_COLUMNS }}
          >
            {STUDIO_COLUMNS.map((col) => (
              <div key={col.id} className="projects-table__header-cell" role="columnheader">
                {col.title}
              </div>
            ))}
          </div>

          {tableListLoading ? (
            <div className="projects-table__loading" aria-busy="true">
              Загрузка таблицы...
            </div>
          ) : tableListError ? (
            <div className="projects-table__empty" role="alert" style={{ color: 'var(--accent-error)' }}>
              Не удалось загрузить студии
            </div>
          ) : studios.length === 0 ? (
            <div className="projects-table__empty" role="status">
              {searchActive ? 'Ничего не найдено' : 'Нет студий'}
            </div>
          ) : (
            studios.map((studio) => {
              const legalName = studio.legal_entity?.name?.trim() || '';
              const notes = studio.notes?.trim() || '';
              return (
                <div key={studio.id} className="projects-table__row-wrapper">
                  <div
                    className="projects-table__row studios-table-catalog__row"
                    role="row"
                    style={{ gridTemplateColumns: STUDIOS_GRID_COLUMNS }}
                  >
                    <div className={STUDIO_COLUMNS[0].cellClassName} role="cell">
                      <StudioEditableCell studio={studio} field="name">
                        {highlightPlainText(
                          studio.name,
                          searchHighlightWords,
                          SEARCH_HIGHLIGHT_CLASS_READERS_CATALOG
                        )}
                      </StudioEditableCell>
                    </div>
                    <div className={STUDIO_COLUMNS[1].cellClassName} role="cell">
                      <StudioEditableCell studio={studio} field="legal_entity">
                        {legalName ? (
                          highlightPlainText(
                            legalName,
                            searchHighlightWords,
                            SEARCH_HIGHLIGHT_CLASS_READERS_CATALOG
                          )
                        ) : (
                          <EmptyNa />
                        )}
                      </StudioEditableCell>
                    </div>
                    <div className={STUDIO_COLUMNS[2].cellClassName} role="cell">
                      <StudioEditableCell studio={studio} field="notes">
                        {notes ? (
                          highlightPlainText(
                            notes,
                            searchHighlightWords,
                            SEARCH_HIGHLIGHT_CLASS_READERS_CATALOG
                          )
                        ) : (
                          <EmptyNa />
                        )}
                      </StudioEditableCell>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </OverlayScrollArea>

      {listQuery.hasNextPage ? (
        <div className="studios-table-catalog__search-more">
          <Button
            type="button"
            variant="outlined"
            size="m"
            disabled={listQuery.isFetchingNextPage}
            onClick={() => void listQuery.fetchNextPage()}
          >
            {listQuery.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
